import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

// ─────────────────────────────────────────────────────────────────────────────
// Types publics — à exporter depuis votre index.ts si nécessaire
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Type de cellule skeleton.
 * Chaque valeur correspond à une variante visuelle rendue dans le template.
 *
 * | Valeur    | Rendu                                      |
 * |-----------|---------------------------------------------|
 * | text      | Barre courte (120 px) — nom, identifiant    |
 * | text-wide | Barre large (180 px) — email, description   |
 * | badge     | Pastille arrondie (72 px) — statut, label   |
 * | toggle    | Pilule (80 px) — interrupteur on/off        |
 * | avatar    | Cercle (32 px) — photo de profil            |
 * | actions   | Barre (96 px) — zone icônes d'action        |
 * | number    | Barre étroite (60 px) — montant, quantité   |
 * | date      | Barre moyenne (100 px) — date, heure        |
 * | checkbox  | Carré (16 px) — case à cocher               |
 * | custom    | Utilise `customWidth` / `customHeight`      |
 */
export type SkeletonCellType =
  | 'text'
  | 'text-wide'
  | 'badge'
  | 'toggle'
  | 'avatar'
  | 'actions'
  | 'number'
  | 'date'
  | 'checkbox'
  | 'custom';

/** Description d'une colonne skeleton */
export interface SkeletonColumn {
  /** Type visuel de la cellule (défaut : 'text') */
  type?: SkeletonCellType;
  /** Largeur CSS forcée — utilisée uniquement si type === 'custom' */
  customWidth?: string;
  /** Hauteur CSS forcée — utilisée uniquement si type === 'custom' */
  customHeight?: string;
  /**
   * Largeur de la colonne `<th>`.
   * Accepte n'importe quelle valeur CSS : '40px', '15%', 'auto', etc.
   */
  colWidth?: string;
  /** Alignement horizontal du contenu (défaut : 'left') */
  align?: 'left' | 'center' | 'right';
}

// ─────────────────────────────────────────────────────────────────────────────
// Dimensions prédéfinies — synchronisées avec le SCSS
// ─────────────────────────────────────────────────────────────────────────────

interface CellDimensions {
  width: string;
  height: string;
  borderRadius: string;
}

const CELL_DIMENSIONS: Record<SkeletonCellType, CellDimensions> = {
  'text':       { width: '120px', height: '14px', borderRadius: '6px'  },
  'text-wide':  { width: '180px', height: '14px', borderRadius: '6px'  },
  'badge':      { width: '72px',  height: '24px', borderRadius: '20px' },
  'toggle':     { width: '80px',  height: '20px', borderRadius: '10px' },
  'avatar':     { width: '32px',  height: '32px', borderRadius: '50%'  },
  'actions':    { width: '96px',  height: '20px', borderRadius: '4px'  },
  'number':     { width: '60px',  height: '14px', borderRadius: '6px'  },
  'date':       { width: '100px', height: '14px', borderRadius: '6px'  },
  'checkbox':   { width: '16px',  height: '16px', borderRadius: '3px'  },
  'custom':     { width: '80px',  height: '14px', borderRadius: '6px'  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Composant
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ## SkeletonTableComponent — `<app-skeleton-table>`
 *
 * Remplace **n'importe quelle table** par son état de chargement skeleton
 * via une liste de colonnes déclarée en `@Input()`.
 *
 * ### Usage minimal
 * ```html
 * <app-skeleton-table
 *   [columns]="['text', 'text-wide', 'badge', 'toggle', 'actions']"
 *   [rows]="5">
 * </app-skeleton-table>
 * ```
 *
 * ### Usage avancé (avec headers)
 * ```ts
 * columns: SkeletonColumn[] = [
 *   { type: 'text',      colWidth: '200px' },
 *   { type: 'text-wide', colWidth: '30%'   },
 *   { type: 'badge',     align: 'center'   },
 *   { type: 'toggle',    align: 'center'   },
 *   { type: 'actions',   colWidth: '120px', align: 'right' },
 * ];
 * ```
 * ```html
 * <app-skeleton-table
 *   [columns]="columns"
 *   [headers]="['Nom', 'Email', 'Statut', 'Activé', 'Actions']"
 *   [rows]="8"
 *   [showProgressBar]="true"
 *   tableClass="table">
 * </app-skeleton-table>
 * ```
 *
 * ### Raccourci — tableau homogène
 * Tous les inputs acceptent aussi une valeur scalaire :
 * ```html
 * <!-- 6 colonnes de type 'text', 4 lignes -->
 * <app-skeleton-table [columnCount]="6" [rows]="4" />
 * ```
 */
@Component({
  selector: 'app-skeleton-table',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './skeleton-table.component.html',
  styleUrls: ['./skeleton-table.component.scss'],
})
export class SkeletonTableComponent implements OnChanges {

  // ── Inputs publics ───────────────────────────────────────────────────────

  /**
   * Définition des colonnes.
   *
   * Accepte :
   * - `SkeletonColumn[]`       — configuration complète
   * - `SkeletonCellType[]`     — raccourci, sans options
   * - `string[]`               — alias (même valeurs que SkeletonCellType)
   *
   * Si absent, utilisé conjointement avec `columnCount`.
   */
  @Input() columns: (SkeletonColumn | SkeletonCellType | string)[] | null = null;

  /**
   * Génère automatiquement N colonnes de type `defaultCellType`.
   * Ignoré si `columns` est fourni.
   */
  @Input() columnCount: number = 5;

  /**
   * Type utilisé par défaut lors de la génération automatique
   * via `columnCount`, ou quand une colonne n'a pas de type défini.
   */
  @Input() defaultCellType: SkeletonCellType = 'text';

  /** Nombre de lignes skeleton à afficher (défaut : 5) */
  @Input() rows: number = 5;

  /**
   * Labels du `<thead>`.
   * - Si fourni et non vide → header visible avec les labels
   * - Si tableau vide `[]`  → header visible sans labels (cellules vides)
   * - Si absent / `null`    → pas de `<thead>` rendu
   */
  @Input() headers: string[] | null = null;

  /**
   * Affiche la barre de progression animée sous le header.
   * N'a d'effet que si `headers` est fourni.
   */
  @Input() showProgressBar: boolean = true;

  /**
   * Classes CSS appliquées sur la balise `<table>`.
   * Permet de réutiliser exactement les styles de votre table cible.
   * Exemple : `'table'`, `'table table--compact'`
   */
  @Input() tableClass: string = 'table';

  /**
   * Classe CSS appliquée sur chaque `<tr>` skeleton.
   * Par défaut : `'skeleton-row'` (synchronisé avec le SCSS existant).
   */
  @Input() rowClass: string = 'skeleton-row';

  // ── État interne calculé ─────────────────────────────────────────────────

  /** Colonnes normalisées, calculées dans `ngOnChanges` */
  resolvedColumns: SkeletonColumn[] = [];

  /** Tableau d'indices pour le `*ngFor` des lignes */
  rowIndexes: number[] = [];

  // ── Lifecycle ────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns'] || changes['columnCount'] || changes['defaultCellType']) {
      this.resolvedColumns = this.resolveColumns();
    }
    if (changes['rows']) {
      this.rowIndexes = Array.from({ length: Math.max(1, this.rows) }, (_, i) => i);
    }
  }

  // ── Méthodes publiques (utilisées dans le template) ───────────────────────

  /**
   * Retourne les styles `inline` de la cellule skeleton selon son type.
   * Les dimensions sont définies dans `CELL_DIMENSIONS` et peuvent être
   * surchargées via `customWidth` / `customHeight` pour le type `'custom'`.
   */
  getCellStyle(col: SkeletonColumn): Record<string, string> {
    const type = col.type ?? this.defaultCellType;
    const dims = { ...CELL_DIMENSIONS[type] };

    if (type === 'custom') {
      if (col.customWidth)  dims.width        = col.customWidth;
      if (col.customHeight) dims.height       = col.customHeight;
    }

    return {
      width:        dims.width,
      height:       dims.height,
      'border-radius': dims.borderRadius,
    };
  }

  /** Retourne les styles `inline` du `<th>` selon la config de la colonne */
  getThStyle(col: SkeletonColumn): Record<string, string> {
    const style: Record<string, string> = {};
    if (col.colWidth) style['width'] = col.colWidth;
    if (col.align)    style['text-align'] = col.align;
    return style;
  }

  /** Retourne les styles `inline` du `<td>` selon l'alignement */
  getTdStyle(col: SkeletonColumn): Record<string, string> {
    return col.align ? { 'text-align': col.align } : {};
  }

  // ── Méthodes privées ──────────────────────────────────────────────────────

  private resolveColumns(): SkeletonColumn[] {
    // Priorité 1 : `columns` fourni explicitement
    if (this.columns && this.columns.length > 0) {
      return this.columns.map(col => this.normalizeColumn(col));
    }

    // Priorité 2 : génération automatique via `columnCount`
    return Array.from(
      { length: Math.max(1, this.columnCount) },
      () => ({ type: this.defaultCellType } as SkeletonColumn),
    );
  }

  private normalizeColumn(col: SkeletonColumn | SkeletonCellType | string): SkeletonColumn {
    // Raccourci : string → objet minimal
    if (typeof col === 'string') {
      return { type: col as SkeletonCellType };
    }
    // Objet déjà complet
    return col;
  }
}